import { HttpClient, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { DisputeCaseType } from "src/app/shared/models/dispute-resolution/dispute-base-models";

/**
 * Export endpoints for every dispute type, behind one service.
 *
 * All seven types expose the same two routes under their own controller prefix, so a single
 * `DisputeCaseType` -> route-segment map covers them without a per-type method. Adding a dispute
 * type to the export feature means adding a row to `routeSegments` and, for all but one pair,
 * nothing else.
 *
 * The exception is Parent Engagement and Early Dispute Resolution, the only two types that share a
 * route segment. Because the map is not injective for them, the segment alone cannot tell the server
 * which of the two lists the user was looking at — so those two also need a row in
 * `earlyDisputeResolutionFlag` below, and the flag travels in the list-export body.
 *
 * Both methods use `observe: "response"` rather than the default body-only observation because the
 * download file name arrives in the `Content-Disposition` header — the body alone cannot tell the
 * caller what to name the file.
 */
@Injectable({
  providedIn: "root",
})
export class DisputeExportService {
  /**
   * Maps a dispute type to the controller route segment that serves its export endpoints. Verified
   * against the `[Route("api/dispute-resolution/...")]` attributes on the DR controllers.
   *
   * Early Dispute Resolution intentionally maps to `parent-engagement`: it has no controller,
   * no frontend service, and no permission enum of its own — it shares Parent Engagement's
   * throughout (see `ParentEngagementService.baseUrl`, and `DisputeCaseFactoryService`, which omits
   * EDR for the same reason). An `early-dispute-resolution` segment would 404.
   */
  private static readonly routeSegments = new Map<DisputeCaseType, string>([
    [DisputeCaseType.StateComplaint, "state-complaint"],
    [DisputeCaseType.DueProcess, "due-process"],
    [DisputeCaseType.Mediation, "mediation"],
    [DisputeCaseType.IEPFacilitation, "iep-facilitation"],
    [DisputeCaseType.AreaOfConcern, "area-of-concern"],
    [DisputeCaseType.ParentEngagement, "parent-engagement"],
    [DisputeCaseType.EarlyDisputeResolution, "parent-engagement"],
  ]);

  /**
   * The case-level `IsEarlyDisputeResolution` value each of the two PE-family types stands for.
   *
   * Only these two types appear here, and only because they share the `parent-engagement` segment:
   * they are one set of tables distinguished by a flag on the case row, so a posted set of ids could
   * otherwise mix them and the workbook would label them as one type. A type absent from this map
   * sends no flag at all, which is why the other five bodies are unchanged.
   */
  private static readonly earlyDisputeResolutionFlag = new Map<DisputeCaseType, boolean>([
    [DisputeCaseType.ParentEngagement, false],
    [DisputeCaseType.EarlyDisputeResolution, true],
  ]);

  /**
   * The types whose exports must ask for confidential cases.
   *
   * Area of Concern is the only type with a confidentiality flag on the case, and its export excludes
   * confidential rows unless the request asks for them. An SEA caller sees those rows on the record
   * list, so not asking produced a workbook missing exactly the cases the user was looking at — a
   * zero-row file when every case in scope was confidential, and a 404 from the single-case route,
   * which surfaced as "Unable to build the export. Please try again."
   *
   * Sending the flag unconditionally grants nothing: the server honours it only for State Education
   * Agency callers and silently ignores it for everyone else, so an AU caller's export is unchanged.
   * Included rows stay identifiable through the `Confidential` column, which every AoC export carries.
   *
   * A set rather than an inline `if` so a second type with the same gate is a one-line change, and so
   * `dispute-export.service.spec.ts` can assert the other six types send nothing.
   */
  private static readonly includeConfidentialTypes = new Set<DisputeCaseType>([
    DisputeCaseType.AreaOfConcern,
  ]);

  constructor(private http: HttpClient) {}

  /**
   * Exports the given cases as one workbook. The ids describe what the user is asking for — the
   * server re-verifies each one against the caller's Administrative Unit scope and drops any it
   * does not own, so this array is a request, never an authorization.
   */
  exportList(caseType: DisputeCaseType, caseIds: string[]): Observable<HttpResponse<Blob>> {
    const isEarlyDisputeResolution =
      DisputeExportService.earlyDisputeResolutionFlag.get(caseType);

    // `undefined` for the five types that do not share a segment: their request bodies stay exactly
    // as they were, rather than gaining a property their DTOs would ignore.
    const body: Record<string, unknown> =
      isEarlyDisputeResolution === undefined
        ? { caseIds }
        : { caseIds, isEarlyDisputeResolution };

    if (DisputeExportService.includeConfidentialTypes.has(caseType)) {
      body["includeConfidential"] = true;
    }

    return this.http.post(`${this.baseUrl(caseType)}/export`, body, {
      responseType: "blob",
      observe: "response",
    });
  }

  /** Exports a single case as a workbook with the same sheet contract as the list export. */
  exportCase(caseType: DisputeCaseType, caseId: string): Observable<HttpResponse<Blob>> {
    // The single-case route takes the flag on the query string rather than in a body, being a GET.
    const query = DisputeExportService.includeConfidentialTypes.has(caseType)
      ? "?includeConfidential=true"
      : "";

    return this.http.get(`${this.baseUrl(caseType)}/${caseId}/export${query}`, {
      responseType: "blob",
      observe: "response",
    });
  }

  private baseUrl(caseType: DisputeCaseType): string {
    const segment = DisputeExportService.routeSegments.get(caseType);

    if (!segment) {
      throw new Error(
        `No export endpoint is defined for dispute case type ${DisputeCaseType[caseType] ?? caseType}.`
      );
    }

    return `/api/dispute-resolution/${segment}`;
  }
}
